import { Option, Options, LeadInputValues, WebService } from '../services/web-service.js';
import {
	Configuration,
	WheelTypes,
	LeatherTypes,
	PaintTypes,
	BespokePaintColours,
	CaliperOptions,
	SeatBackStyles,
	GearStickOptions,
	HardTopOptions,
	AudioOptions,
	UpholsteryContrastOptions
} from '../data/configuration.js';
import { Utilities } from '../utilities/utilities.ts'
import { ModalContent } from '../components/ui/modal.tsx'

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export type SupportedRegion = {
	id: string,
	name: string,
}

export type DealerDetails = {
	id: string,
	name?: string,
	subtitle?: string,
	address1?: string,
	address2?: string,
	address3?: string,
	address4?: string,
	postcode?: string,
	country?: string,
	telephone?: string,
	email?: string,
	lat?: string,
	lng?: string,
	company?: string,
}

/**
 * Cloud Data:
 */
export abstract class CloudData {

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	private static _modelOptions: Options;
	private static _transmissionOptions: Options;
	private static _paintworkColourOptions: Options;
	private static _wheelStyleOptions: Options;
	private static _leatherStyleOptions: Options;
	private static _seatBackStyleOptions: Options;
	private static _leatherPipingOptions: Options;
	private static _seatStitchingOptions: Options;
	private static _carpetColourOptions: Options;
	private static _dashboardOptions: Options;
	private static _steeringWheelOptions: Options;
	private static _hoodStyleOptions: Options;
	private static _hardTopOptions: Options;
	private static _seatPleatingOptions: Options;
	private static _headrestEmbroideryOptions: Options;
	private static _centreConsoleOptions: Options;
	private static _stoneGuardOptions: Options;
	private static _grilleMeshOptions: Options;
	private static _graphicsOptions: Options;
	private static _badgeOptions: Options;
	private static _accessoryOptions: Options;

	// the list of supported countries/regions, used to populate the country drop-down and to validate the auto-detected region.
	// n.b. we could also load this from the cloud, but Morgan may wish to only support certain countries, so we will hard-code the list for now.
	private static _supportedRegions: SupportedRegion[] = [
		{ id: 'gb', name: 'United Kingdom' },
		{ id: 'us', name: 'USA' },
		{ id: 'au', name: 'Australia' },
		{ id: 'at', name: 'Austria' },
		{ id: 'be', name: 'Belgium' },
		{ id: 'fr', name: 'France' },
		{ id: 'de', name: 'Germany' },
		{ id: 'it', name: 'Italy' },
		{ id: 'jp', name: 'Japan' },
		{ id: 'lu', name: 'Luxembourg' },
		{ id: 'nl', name: 'Netherlands' },
		{ id: 'no', name: 'Norway' },
		{ id: 'pl', name: 'Poland' },
		{ id: 'pt', name: 'Portugal' },
		{ id: 'es', name: 'Spain' },
		{ id: 'se', name: 'Sweden' },
		{ id: 'ch', name: 'Switzerland' }
	];

	//-----------------------------------------------------------------------------------------
	// Properties:
	//-----------------------------------------------------------------------------------------

	static get modelOptions() {
		return this._modelOptions;
	}

	static get transmissionOptions() {
		return this._transmissionOptions;
	}

	static get paintworkColourOptions() {
		return this._paintworkColourOptions;
	}
	
	static get wheelStyleOptions() {
		return this._wheelStyleOptions;
	}

	static get leatherStyleOptions() {
		return this._leatherStyleOptions;
	}

	static get seatBackStyleOptions() {
		return this._seatBackStyleOptions;
	}

	static get leatherPipingOptions() {
		return this._leatherPipingOptions;
	}

	static get seatStitchingOptions() {
		return this._seatStitchingOptions;
	}

	static get carpetColourOptions() {
		return this._carpetColourOptions;
	}

	static get dashboardOptions() {
		return this._dashboardOptions;
	}

	static get steeringWheelOptions() {
		return this._steeringWheelOptions;
	}

	static get hoodStyleOptions() {
		return this._hoodStyleOptions;
	}

	static get hardTopOptions() {
		return this._hardTopOptions;
	}

	static get seatPleatingOptions() {
		return this._seatPleatingOptions;
	}

	static get headrestEmbroideryOptions() {
		return this._headrestEmbroideryOptions;
	}

	static get centreConsoleOptions() {
		return this._centreConsoleOptions;
	}

	static get graphicsOptions() {
		return this._graphicsOptions;
	}

	static get badgeOptions() {
		return this._badgeOptions;
	}

	static get accessoryOptions() {
		return this._accessoryOptions;
	}

	static get supportedRegions() {
		return this._supportedRegions;
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	// an async function that loads options data from the cloud, populating the above fields and returning a promise when done.
	static async loadOptions(modelId: string, regionCode: string, languageCode: string): Promise<void> {
		await WebService.getModels(regionCode, languageCode).then(results => this._modelOptions = results);
		await WebService.getOptions('TransmissionOptions', modelId, regionCode, languageCode).then(results => this._transmissionOptions = results);
		await WebService.getOptions('PaintworkColours', modelId, regionCode, languageCode).then(results => this._paintworkColourOptions = results);
		await WebService.getOptions('WheelStyles', modelId, regionCode, languageCode).then(results => this._wheelStyleOptions = results);
		await WebService.getOptions('LeatherGrainStyles', modelId, regionCode, languageCode).then(results => this._leatherStyleOptions = results);
		await WebService.getOptions('SeatBackOptions', modelId, regionCode, languageCode).then(results => this._seatBackStyleOptions = results);
		await WebService.getOptions('LeatherPiping', modelId, regionCode, languageCode).then(results => this._leatherPipingOptions = results);
		await WebService.getOptions('SeatStitching', modelId, regionCode, languageCode).then(results => this._seatStitchingOptions = results);
		await WebService.getOptions('CarpetColours', modelId, regionCode, languageCode).then(results => this._carpetColourOptions = results);
		await WebService.getOptions('DashboardOptions', modelId, regionCode, languageCode).then(results => this._dashboardOptions = results);
		await WebService.getOptions('SteeringWheelCentreStyles', modelId, regionCode, languageCode).then(results => this._steeringWheelOptions = results);
		await WebService.getOptions('HoodStyles', modelId, regionCode, languageCode).then(results => this._hoodStyleOptions = results);
		await WebService.getOptions('HardtopOptions', modelId, regionCode, languageCode).then(results => this._hardTopOptions = results);
		await WebService.getOptions('SeatPleatingOptions', modelId, regionCode, languageCode).then(results => this._seatPleatingOptions = results);
		await WebService.getOptions('HeadrestEmbroidery', modelId, regionCode, languageCode).then(results => this._headrestEmbroideryOptions = results);
		await WebService.getOptions('CentreColumnOptions', modelId, regionCode, languageCode).then(results => this._centreConsoleOptions = results);
		await WebService.getOptions('StoneGuardStyles', modelId, regionCode, languageCode).then(results => this._stoneGuardOptions = results);
		await WebService.getOptions('GrilleMeshStyles', modelId, regionCode, languageCode).then(results => this._grilleMeshOptions = results);
		await WebService.getOptions('GraphicsOptions', modelId, regionCode, languageCode).then(results => this._graphicsOptions = results);
		await WebService.getOptions('Badges', modelId, regionCode, languageCode).then(results => this._badgeOptions = results);
		await WebService.getOptions('Options', modelId, regionCode, languageCode).then(results => this._accessoryOptions = results);

		// add tunnel side options to leather style options.
		await WebService.getOptions('LeatherOptions', modelId, regionCode, languageCode).then(results => this._leatherStyleOptions = { ...this._leatherStyleOptions, ...results });
		// n.b. the luggage rack, heated seat, audio options, and exhaust styles are appended to the accessory options.
		await WebService.getOptions('LuggageRackOptions', modelId, regionCode, languageCode).then(results => this._accessoryOptions = { ...this._accessoryOptions, ...results });
		await WebService.getOptions('HeatedSeatOptions', modelId, regionCode, languageCode).then(results => this._accessoryOptions = { ...this._accessoryOptions, ...results });
		await WebService.getOptions('AudioOptions', modelId, regionCode, languageCode).then(results => this._accessoryOptions = { ...this._accessoryOptions, ...results });
		await WebService.getOptions('ExhaustStyles', modelId, regionCode, languageCode).then(results => this._accessoryOptions = { ...this._accessoryOptions, ...results });
	}

	static isValidOption = (obj: object, id: string): boolean => {
		return Object.values(obj).includes(id);
	}

	// checks whether the given region code is one of the supported countries/regions.
	static isSupportedRegion(regionCode: string): boolean {
		return this._supportedRegions.some(region => region.id === regionCode);
	}

	// an async function that loads configuration data from the cloud, returning a promise when done.
	static async loadConfiguration(shortCode: string): Promise<void> {
		await WebService.loadConfiguration(shortCode).then(result => {

			// reference the relevant nodes.
			const options = result.options;
			const model = options.model;
			const colourways = options.paintwork.colourways;
			const interior = options.interior_textiles;
			const leather = interior.leather;
			const seats = interior.seats;
			const cockpit = options.cockpit;
			const exterior = options.exterior_textiles;

			// ensure the loaded configuration is for this car model.
			if (model.type.id !== Configuration.modelId) {
				throw new Error('ShortCode provided is for a different model');
			}

			// ensure the required loaded options are valid.

			if (!this._paintworkColourOptions[colourways.paint_body.id]) {
				throw new Error('Invalid body paint ID loaded');
			}
			if (!this._wheelStyleOptions[options.wheels.style.id]) {
				throw new Error('Invalid wheel style ID loaded');
			}
			if (!this._seatBackStyleOptions[seats.seat_back.id]) {
				throw new Error('Invalid seat back style ID loaded');
			}
			if (!this._seatStitchingOptions[interior.stitching.id]) {
				throw new Error('Invalid seat stitching style ID loaded');
			}
			if (!this._carpetColourOptions[interior.carpet.id]) {
				throw new Error('Invalid carpet style ID loaded');
			}
			if (!this._dashboardOptions[cockpit.dashboard_face.id]) {
				throw new Error('Invalid dashboard style ID loaded');
			}
			if (!this._steeringWheelOptions[cockpit.steering_wheel_centre.id]) {
				throw new Error('Invalid steering wheel ID loaded');
			}
			if (!this._steeringWheelOptions[cockpit.steering_wheel_column.id]) {
				throw new Error('Invalid steering column ID loaded');
			}
			if (!this._hoodStyleOptions[exterior.hood.id]) {
				throw new Error('Invalid hood style ID loaded');
			}
			if (!this._seatPleatingOptions[interior.seats.pleating.id]) {
				throw new Error('Invalid seat pleating style ID loaded');
			}
			if (!this._headrestEmbroideryOptions[interior.seats.headrest_embroidery.id]) {
				throw new Error('Invalid headrest embroidery style ID loaded');
			}
			if (!this._centreConsoleOptions[cockpit.cockpit_wood.id]) {
				throw new Error('Invalid cockpit wood ID loaded');
			}

			//
			// update the configuration.
			//

			// paint --------------------------------------------------------------------

			Configuration.bodyPaintColour = colourways.paint_body.id;

			// a bespoke paint option carries its colour as a hex alongside the ID, so recover it here - the ID alone gives the finish.
			// a standard colour clears it, so that a bespoke colour from earlier in the session is not left applied over the loaded paint.
			Configuration.bespokePaintColour = null;
			if (Configuration.isBespokePaintColour(colourways.paint_body.id)) {

				// n.b. a missing or malformed hex is not fatal: the option keeps its description and price, and the vehicle is left
				// on its existing paint rather than the load being rejected. configurations saved before 'option_hex' existed land here.
				const bespokeColour = colourways.paint_body.option_hex ? Utilities.hexToRgba(colourways.paint_body.option_hex) : null;
				if (!bespokeColour) {
					console.error('No valid bespoke colour hex loaded for paint ID: ', colourways.paint_body.id);
				}
				Configuration.bespokePaintColour = bespokeColour;
			}

			// wheels --------------------------------------------------------------------

			Configuration.wheelStyle = options.wheels.style.id;

			// front caliper option is only applicable for sub-model 400, so check validity before applying.
			if (this.isValidOption(CaliperOptions, options.wheels?.caliper_option?.id)) {
				Configuration.frontBrakeCaliperOption = options.wheels.caliper_option.id;
			}

			// interior ------------------------------------------------------------------

			// materials.

			// a leather that has been discontinued, or that the back office no longer lists, falls back to the default rather than the whole
			// configuration being rejected. n.b. discontinued IDs are checked explicitly, as the back office may still list them.
			if (this._leatherStyleOptions[leather.style.id] && !Configuration.discontinuedLeatherStyleIds.includes(leather.style.id)) {
				Configuration.leatherStyle = leather.style.id;
			} else {
				console.error('Invalid or discontinued leather style ID loaded, using default: ', leather.style.id);
				Configuration.leatherStyle = Configuration.getDefaultLeatherStyle();
			}

			Configuration.tunnelSidesStyle = leather.trim.id;

			Configuration.seatPleatingOption = seats?.pleating?.id;

			if (this.isValidOption(SeatBackStyles, seats?.seat_back?.id)) {
				Configuration.seatBackStyle = seats?.seat_back?.id;
			} else {
				Configuration.seatBackStyle = SeatBackStyles.TwoToneTextile;
			}

			// a two-tone leather on the standard model dictates its own seat back and pleating styles (as in the leather view), so re-apply
			// them here - the default leather above is two-tone, but the configuration it replaced may have been saved with a single-colour one.
			if (!Configuration.isSupersport400Model() && this.getLeatherTypesForId(Configuration.leatherStyle).leatherType === LeatherTypes.TwoTone) {
				Configuration.seatBackStyle = SeatBackStyles.TwoToneTextile;
				Configuration.seatPleatingOption = Configuration.seatPleatingOptionForTwoToneLeather;
			}

			Configuration.headrestEmbroideryOption = interior.seats.headrest_embroidery.id;

			Configuration.seatStitchingOption = interior.stitching.id;

			Configuration.carpetColour = interior.carpet.id;

			// upholstery contrast option is only applicable for sub-model 400.
			if (Configuration.isSupersport400Model() && this.isValidOption(UpholsteryContrastOptions, leather?.contrast_option?.id)) {
				Configuration.upholsteryContrastOption = leather.contrast_option.id;
			}

			// cockpit.

			Configuration.dashboardOption = options.cockpit.dashboard_face.id;
			Configuration.steeringWheelOption = options.cockpit.steering_wheel_centre.id;
			Configuration.steeringColumnOption = options.cockpit.steering_wheel_column.id;
			Configuration.centreConsoleOption = options.cockpit.cockpit_wood.id;

			// gear stick option is only applicable for sub-model 400.
			if (Configuration.isSupersport400Model() && this.isValidOption(GearStickOptions, options.cockpit?.gear_selector?.id)) {
				Configuration.gearStickOption = options.cockpit.gear_selector.id;
			}
			
			// exterior ------------------------------------------------------------------

			// textiles.

			Configuration.hoodStyleOption = exterior.hood.id;

			if (this.isValidOption(HardTopOptions, exterior?.hardtop?.id)) {
				Configuration.hardTopOption = exterior.hardtop.id as HardTopOptions;
			}

			// graphics.

			if (options.final_finishes.graphics?.length) {
				Configuration.doorGraphicOption = options.final_finishes.graphics[0]?.id;
			}

			// stripe and sill graphics options are only applicable for sub-model 400.
			if (Configuration.isSupersport400Model()) {
				if (options.final_finishes.stripe_graphics?.length) {
					Configuration.stripeGraphicOption = options.final_finishes.stripe_graphics[0]?.id;
				}
				if (options.final_finishes.sill_graphics?.length) {
					Configuration.sillGraphicOption = options.final_finishes.sill_graphics[0]?.id;
				}
			}

			// final finishes.

			if (options.final_finishes.accents?.length) {
				Configuration.accentPackOption = options.final_finishes?.accents?.[0]?.id;
			}

			if (options.final_finishes.paint_protection_film?.id) {
				Configuration.paintProtectionFilmOption = options.final_finishes.paint_protection_film.id;
			}

			// accessories ------------------------------------------------------------------

			// storage.

			options.accessories?.storage_bags?.forEach((storageItem) => {
				Configuration.addAccessoryOption(Configuration.accessoryStorageOptions, storageItem.id);
			});

			const tunnelStorage = options.cockpit.convenience.find((item) => item.id === Configuration.tunnelStorageOption);
			if (tunnelStorage) {
				Configuration.addAccessoryOption(Configuration.accessoryStorageOptions, tunnelStorage.id);
			}

			// covers.

			options.accessories?.covers?.forEach((cover) => {
				Configuration.addAccessoryOption(Configuration.accessoryCoversOptions, cover.id);
			});

			// equipment.

			options.accessories?.equipment?.forEach((equipmentItem) => {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, equipmentItem.id);
			});

			if (seats?.heated_seats?.id) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, seats.heated_seats.id);
			}

			if (seats?.comfort_seats?.id) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, seats.comfort_seats.id);
			}

			const connectivityPack = options.cockpit.convenience.find((item) => item.id === Configuration.connectivityPackOption);
			if (connectivityPack) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, connectivityPack.id);
			}

			const airCon = options.cockpit.convenience.find((item) => item.id === Configuration.airConOption);
			if (airCon) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, airCon.id);
			}

			const cupHolder = options.cockpit.convenience.find((item) => item.id === Configuration.cupHolderOption);
			if (cupHolder) {
				Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, cupHolder.id);
			}

			// services.

			options.accessories?.services?.forEach((servicesItem) => {
				Configuration.addAccessoryOption(Configuration.accessoryServicesOptions, servicesItem.id);
			});

			// aftermarket.

			options.accessories?.aftermarket?.forEach((aftermarketItem) => {
				Configuration.addAccessoryOption(Configuration.accessoryAftermarketOptions, aftermarketItem.id);
			});

			// audio.

			const audio = options.cockpit.convenience.find((item) => item.id === AudioOptions.Bluetooth || item.id === AudioOptions.Sennheiser);
			if (audio) {
				Configuration.audioOption = audio.id;
			}

		});
	}
	
	// configure accessory options.
	static handleSavedAccessoryOptions(options: Set<string>) {

		// return if invalid.
		if (!(options instanceof Set)) {
			console.error('Invalid options:', options);
			return [];
		}

		// map over the options array and return a new array of objects.
		return Array.from(options).map((option) => {

			const price = this.getPrice(option,
				this.accessoryOptions,
				{
					ref: false
				});
			return {
				id: option,
				name: this.getName(option, this._accessoryOptions),
				price: Utilities.asCurrency(price, Configuration.regionCode),
			};
		});
	}

	// configure summary item.
	static configureSummaryItem(options: Options, optionCode: string, rowName?: string) {

		// return null if no option code.
		if (!optionCode || optionCode === 'NONE' || optionCode === 'N/A') return null;

		const price = this.getPrice(optionCode,
			options,
			{
				ref: false
			});


		// return a configured object for each summary item.
		return {
			name: rowName ? rowName : '',
			option_code: optionCode,
			value: this.getName(optionCode, options),
			price: Utilities.asCurrency(price, Configuration.regionCode)
		};
	}

	// generate the summary array.
	static async generateSummary() {

		const configureSummaryItem = this.configureSummaryItem.bind(this);

		// model.

		const model = {
			group: 'Model',
			items: [
				configureSummaryItem(this.modelOptions, Configuration.modelId, 'Model')
			].filter(Boolean)
		}

		// paintwork.

		const paintwork = {
			group: 'Paintwork',
			items: [
				configureSummaryItem(this.paintworkColourOptions, Configuration.bodyPaintColour, 'Paint')
			].filter(Boolean)
		}

		// wheels.

		const wheelId = Configuration.wheelStyle;

		const wheels = {
			group: 'Wheels',
			items: [
				configureSummaryItem(this.wheelStyleOptions, wheelId, 'Wheel Style')
			].filter(Boolean)
		}
		if (Configuration.isSupersport400Model()) {
			wheels.items.push(configureSummaryItem(this.accessoryOptions, Configuration.frontBrakeCaliperOption, 'Front Brake Caliper'));
		}

		// interior textiles.

		const interiorTextiles = {
			group: 'Interior Textiles',
			items: [
				configureSummaryItem(this.leatherStyleOptions, Configuration.leatherStyle, 'Leather'),
				configureSummaryItem(this.seatPleatingOptions, Configuration.seatPleatingOption, 'Pleating'),
				configureSummaryItem(this.seatBackStyleOptions, Configuration.seatBackStyle, 'Seat Backs'),
				configureSummaryItem(this.headrestEmbroideryOptions, Configuration.headrestEmbroideryOption, 'Headrest Embroidery'),
				configureSummaryItem(this.seatStitchingOptions, Configuration.seatStitchingOption, 'Stitching'),
				configureSummaryItem(this.carpetColourOptions, Configuration.carpetColour, 'Carpet'),
				configureSummaryItem(this.leatherStyleOptions, Configuration.tunnelSidesStyle, 'Tunnel Sides')
			].filter(Boolean)
		}
		if (Configuration.isSupersport400Model()) {

			// may be none, so check before pushing.
			const upholsteryContrastOption = configureSummaryItem(this.leatherStyleOptions, Configuration.upholsteryContrastOption, 'Contrast');
			if (upholsteryContrastOption) {
				interiorTextiles.items.push(upholsteryContrastOption);
			}
		}

		// cockpit.

		const cockpit = {
			group: 'Cockpit',
			items: [
				configureSummaryItem(this.dashboardOptions, Configuration.dashboardOption, 'Dashboard Face'),
				configureSummaryItem(this.centreConsoleOptions, Configuration.centreConsoleOption, 'Centre Console'),
				configureSummaryItem(this.steeringWheelOptions, Configuration.steeringWheelOption, 'Steering Wheel'),
				configureSummaryItem(this.steeringWheelOptions, Configuration.steeringColumnOption, 'Steering Column')
			].filter(Boolean)
		}
		if (Configuration.isSupersport400Model()) {
			cockpit.items.push(configureSummaryItem(this.accessoryOptions, Configuration.gearStickOption, 'Gear Selector'));
		}

		// exterior textiles.

		const exteriorTextiles = {
			group: 'Exterior Textiles',
			items: [
				configureSummaryItem(this.hoodStyleOptions, Configuration.hoodStyleOption, 'Hood'),
				configureSummaryItem(this.hardTopOptions, Configuration.hardTopOption, 'Hard Top'),
				configureSummaryItem(this.graphicsOptions, Configuration.doorGraphicOption, 'Door Graphics'),
				configureSummaryItem(this.accessoryOptions, Configuration.accentPackOption, 'Accent Pack'),
				configureSummaryItem(this.accessoryOptions, Configuration.paintProtectionFilmOption, 'Paint Protection Film')
			].filter(Boolean)
		}
		if (Configuration.isSupersport400Model()) {

			// may be none, so check before pushing.
			const stripeGraphicOption = configureSummaryItem(this.graphicsOptions, Configuration.stripeGraphicOption, 'Stripe Graphics');
			if (stripeGraphicOption) {
				exteriorTextiles.items.push(stripeGraphicOption);
			}
			exteriorTextiles.items.push(configureSummaryItem(this.graphicsOptions, Configuration.sillGraphicOption, 'Sill Graphics'));
		}

		// accessories.

		const storage = Array.from(Configuration.accessoryStorageOptions).map(option => configureSummaryItem(this.accessoryOptions, option));
		const covers = Array.from(Configuration.accessoryCoversOptions).map(option => configureSummaryItem(this.accessoryOptions, option));
		const equipment = Array.from(Configuration.accessoryEquipmentOptions).map(option => configureSummaryItem(this.accessoryOptions, option));
		const services = Array.from(Configuration.accessoryServicesOptions).map(option => configureSummaryItem(this.accessoryOptions, option));
		const aftermarket = Array.from(Configuration.accessoryAftermarketOptions).map(option => configureSummaryItem(this.accessoryOptions, option));

		const accessories = {
			group: 'Accessories',
			items: [
				...storage,
				...covers,
				...equipment,
				...services,
				...aftermarket,
				configureSummaryItem(this.accessoryOptions, Configuration.audioOption, 'Audio')
			].filter(Boolean)
		}

		return [
			model,
			paintwork,
			wheels,
			interiorTextiles,
			cockpit,
			exteriorTextiles,
			accessories
		];
	}

	// generate options to be used when saving and generating a PDF.
	static async generateOptions() {
		
		// handle price universally.
		const handlePrice = (id: string, idOptions: Options) => {

			const price = this.getPrice(id,
				idOptions,
				{
					ref: false
				});

			return price;
		}
		
		// model ------------------------------------------------------------------

		const model = {
			type: {
				id: Configuration.modelId,
				name: Configuration.isSupersport400Model() ? 'Supersport 400' : 'Supersport',
				price: handlePrice(
					Configuration.modelId,
					CloudData.modelOptions),
			}
		}

		// paintwork ------------------------------------------------------------------

		const paintwork: { colourways: { paint_body: any }} = {
			colourways: {
				paint_body: {
					id: Configuration.bodyPaintColour,
					name: this.getName(Configuration.bodyPaintColour, this._paintworkColourOptions),
					lacquer_matt: false,
					price: handlePrice(Configuration.bodyPaintColour, this._paintworkColourOptions),
					laquer_price: 0.00
				}
			}
		};

		// a bespoke paint option's ID says which finish was chosen but not which colour, so the colour travels beside it as a hex.
		if (Configuration.isBespokePaintColour(Configuration.bodyPaintColour) && Configuration.bespokePaintColour) {
			paintwork.colourways.paint_body.option_hex = Utilities.rgbaToHex(Configuration.bespokePaintColour, true);
		}

		// wheels ------------------------------------------------------------------

		const wheels: { style: any, caliper_option?: any } = {

			// wheel style.
			style: {
				id: Configuration.wheelStyle,
				name: this.getName(Configuration.wheelStyle, this._wheelStyleOptions),
				price: handlePrice(Configuration.wheelStyle, this._wheelStyleOptions),
			},

		};

		// caliper option (only for 400 models).
		if (Configuration.isSupersport400Model()) {
			wheels.caliper_option = {
				id: Configuration.frontBrakeCaliperOption,
				name: this.getName(Configuration.frontBrakeCaliperOption, this._accessoryOptions),
				price: handlePrice(Configuration.frontBrakeCaliperOption, this._accessoryOptions),
			};
		}

		// interior textiles ------------------------------------------------------------------

		let seatBackId = Configuration.seatBackStyle;
		if (!Configuration.isSupersport400Model() && seatBackId === SeatBackStyles.TwoToneTextile) {
			seatBackId = 'SB001';
		}

		const interiorTextiles: { leather: any, seats: any, stitching: any, carpet: any } = {

			// leather.
			leather: {
				style: {
					id: Configuration.leatherStyle,
					name: this.getName(Configuration.leatherStyle, this._leatherStyleOptions),
					price: handlePrice(Configuration.leatherStyle, this._leatherStyleOptions),
				},
				trim: {
					id: Configuration.tunnelSidesStyle,
					name: this.getName(Configuration.tunnelSidesStyle, this._leatherStyleOptions),
					price: handlePrice(Configuration.tunnelSidesStyle, this._leatherStyleOptions),
				}
			},

			// seats.
			seats: {
				pleating: {
					id: Configuration.seatPleatingOption,
					name: this.getName(Configuration.seatPleatingOption, this._seatPleatingOptions),
					price: handlePrice(Configuration.seatPleatingOption, this._seatPleatingOptions),
				},
				seat_back: {
					id: seatBackId,
					name: this.getName(Configuration.seatBackStyle, this._seatBackStyleOptions),
					price: handlePrice(Configuration.seatBackStyle, this._seatBackStyleOptions),
				},
				headrest_embroidery: {
					id: Configuration.headrestEmbroideryOption,
					name: this.getName(Configuration.headrestEmbroideryOption, this._headrestEmbroideryOptions),
					price: handlePrice(Configuration.headrestEmbroideryOption, this._headrestEmbroideryOptions),
				}
			},

			// stitching.
			stitching: {
				id: Configuration.seatStitchingOption,
				name: this.getName(Configuration.seatStitchingOption, this._seatStitchingOptions),
				price: handlePrice(Configuration.seatStitchingOption, this._seatStitchingOptions),
			},

			// carpet.
			carpet: {
				id: Configuration.carpetColour,
				name: this.getName(Configuration.carpetColour, this._carpetColourOptions),
				price: handlePrice(Configuration.carpetColour, this._carpetColourOptions),
			}
		};

		// contrast option (only for 400 models).
		if (Configuration.isSupersport400Model() && Configuration.upholsteryContrastOption !== 'NONE') {
			interiorTextiles.leather.contrast_option = {
				id: Configuration.upholsteryContrastOption,
				name: this.getName(Configuration.upholsteryContrastOption, this._leatherStyleOptions),
				price: handlePrice(Configuration.upholsteryContrastOption, this._leatherStyleOptions),
			}
		}
		
		// heated seats.
		if (Configuration.accessoryEquipmentOptions.has(Configuration.heatedSeatsOption)) {
			interiorTextiles.seats.heated_seats = {
				id: Configuration.heatedSeatsOption,
				name: this.getName(Configuration.heatedSeatsOption, this._accessoryOptions),
				price: handlePrice(Configuration.heatedSeatsOption, this._accessoryOptions),
			}
		}

		// comfort plus.
		if (Configuration.accessoryEquipmentOptions.has(Configuration.comfortPlusOption)) {
			interiorTextiles.seats.comfort_seats = {
				id: Configuration.comfortPlusOption,
				name: this.getName(Configuration.heatedSeatsOption, this._accessoryOptions),
				price: handlePrice(Configuration.heatedSeatsOption, this._accessoryOptions),
			}
		}
		
		// cockpit ------------------------------------------------------------------

		const cockpit: { dashboard_face: any, cockpit_wood: any, steering_wheel_centre: any, steering_wheel_column: any, leather_details: any, gear_selector?: any, convenience?: any } = {

			// dashboard face.
			dashboard_face: {
				id: Configuration.dashboardOption,
				name: this.getName(Configuration.dashboardOption, this.dashboardOptions),
				price: handlePrice(Configuration.dashboardOption, this.dashboardOptions),
			},

			// center column.
			cockpit_wood: {
				id: Configuration.centreConsoleOption,
				name: this.getName(Configuration.centreConsoleOption, this.centreConsoleOptions),
				price: handlePrice(Configuration.centreConsoleOption, this._centreConsoleOptions),
				lacquer_matt: true, // all wood options are now Matte.
				lacquer_price: 0.00 // not sure if we stil need to output this option, as wood prices are now inclusive of lacquer.
			},

			// steering wheel centre.
			steering_wheel_centre: {
				id: Configuration.steeringWheelOption,
				name: this.getName(Configuration.steeringWheelOption, this._steeringWheelOptions),
				price: handlePrice(Configuration.steeringWheelOption, this._steeringWheelOptions),
			},
			
			// steering wheel column.
			steering_wheel_column: {
				id: Configuration.steeringColumnOption,
				name: this.getName(Configuration.steeringColumnOption, this._steeringWheelOptions),
				price: handlePrice(Configuration.steeringColumnOption, this._steeringWheelOptions),
			},

			// leather details.
			leather_details: [{
				id: 'LT001',
				name: 'Black',
				price: '\u00a30.00'
			}]
		};

		// need to get out convenience from accessories.
		cockpit.convenience = [];

		// cupholder.
		if (Configuration.accessoryEquipmentOptions.has(Configuration.cupHolderOption)) {
			cockpit.convenience.push({
				id: Configuration.cupHolderOption,
				name: this.getName(Configuration.cupHolderOption, this._accessoryOptions),
				price: handlePrice(Configuration.cupHolderOption, this._accessoryOptions),
			});
		}

		// air con.
		if (Configuration.accessoryEquipmentOptions.has(Configuration.airConOption)) {
			cockpit.convenience.push({
				id: Configuration.airConOption,
				name: this.getName(Configuration.airConOption, this._accessoryOptions),
				price: handlePrice(Configuration.airConOption, this._accessoryOptions),
			});
		}

		// audio.
		cockpit.convenience.push({
			id: Configuration.audioOption,
			name: this.getName(Configuration.audioOption, this._accessoryOptions),
			price: handlePrice(Configuration.audioOption, this._accessoryOptions),
		});

		// tunnel storage.
		if (Configuration.accessoryStorageOptions.has(Configuration.tunnelStorageOption)) {
			cockpit.convenience.push({
				id: Configuration.tunnelStorageOption,
				name: this.getName(Configuration.tunnelStorageOption, this._accessoryOptions),
				price: handlePrice(Configuration.tunnelStorageOption, this._accessoryOptions),
			});
		}

		// gear selector (only for 400 models).
		if (Configuration.isSupersport400Model()) {
			cockpit.gear_selector = {
				id: Configuration.gearStickOption,
				name: this.getName(Configuration.gearStickOption, this._accessoryOptions),
				price: handlePrice(Configuration.gearStickOption, this._accessoryOptions),
			};
		}

		// exterior textiles ------------------------------------------------------------------

		const exteriorTextiles = {

			// hood.
			hood: {
				id: Configuration.hoodStyleOption,
				name: this.getName(Configuration.hoodStyleOption, this._hoodStyleOptions),
				price: handlePrice(Configuration.hoodStyleOption, this._hoodStyleOptions),
			},
			hardtop: {
				id: Configuration.hardTopOption,
				name: this.getName(Configuration.hardTopOption, this._hardTopOptions),
				price: handlePrice(Configuration.hardTopOption, this._hardTopOptions),
			},
		};

		// final finishes ------------------------------------------------------------------

		const finalFinishes: { badges?: any, paint_protection_film?: any, graphics?: any, stripe_graphics?: any, sill_graphics?: any } = {
		};

		// accent packs.

		if (Configuration.accentPackOption !== 'NONE') {
			finalFinishes.badges =  {
				id: Configuration.accentPackOption,
				name: this.getName(Configuration.accentPackOption, this._accessoryOptions),
				price: handlePrice(Configuration.accentPackOption, this._accessoryOptions)
			};
		}

		// paint protection film.

		if (Configuration.paintProtectionFilmOption !== 'NONE') {
			finalFinishes.paint_protection_film = {
				id: Configuration.paintProtectionFilmOption,
				name: this.getName(Configuration.paintProtectionFilmOption, this.accessoryOptions),
				price: handlePrice(Configuration.paintProtectionFilmOption, this.accessoryOptions),
			};
		}

		// graphics.

		if (Configuration.doorGraphicOption !== 'NONE') {

			// door graphic.
			finalFinishes.graphics = [...finalFinishes.graphics || [], {
				id: Configuration.doorGraphicOption,
				name: this.getName(Configuration.doorGraphicOption, this._graphicsOptions),
				price: handlePrice(Configuration.doorGraphicOption, this._graphicsOptions),
			}];
		}

		// stripe and sill graphics (only for 400 models).
		if (Configuration.isSupersport400Model()) {
			if (Configuration.stripeGraphicOption !== 'NONE') {
				finalFinishes.stripe_graphics = [
					...finalFinishes.stripe_graphics || [], {
						id: Configuration.stripeGraphicOption,
						name: this.getName(Configuration.stripeGraphicOption, this._graphicsOptions),
						price: handlePrice(Configuration.stripeGraphicOption, this._graphicsOptions),
					}
				];
			}
			if (Configuration.sillGraphicOption !== 'NONE') {
				finalFinishes.sill_graphics = [
					...finalFinishes.sill_graphics || [], {
						id: Configuration.sillGraphicOption,
						name: this.getName(Configuration.sillGraphicOption, this._graphicsOptions),
						price: handlePrice(Configuration.sillGraphicOption, this._graphicsOptions),
					}
				];
			}
		}

		// accessories ------------------------------------------------------------------

		const accessories: { storage_bags?: any, covers?: any, equipment?: any, services?: any, aftermarket?: any } = {};

		// storage.
		if (Configuration.accessoryStorageOptions.size > 0) {
			accessories.storage_bags = this.handleSavedAccessoryOptions(Configuration.accessoryStorageOptions);
		}

		// covers.
		if (Configuration.accessoryCoversOptions.size > 0) {
			accessories.covers = this.handleSavedAccessoryOptions(Configuration.accessoryCoversOptions);
		}

		// equipment.
		if (Configuration.accessoryEquipmentOptions.size > 0) {
			accessories.equipment = this.handleSavedAccessoryOptions(Configuration.accessoryEquipmentOptions);
		}

		// services.
		if (Configuration.accessoryServicesOptions.size > 0) {
			accessories.services = this.handleSavedAccessoryOptions(Configuration.accessoryServicesOptions);
		}

		// aftermarket.
		if (Configuration.accessoryAftermarketOptions.size > 0) {
			accessories.aftermarket = this.handleSavedAccessoryOptions(Configuration.accessoryAftermarketOptions);
		}

		const options = {
			model,
			paintwork,
			wheels,
			interior_textiles: interiorTextiles,
			cockpit,
			exterior_textiles: exteriorTextiles,
			final_finishes: finalFinishes,
			accessories
		}

		return options;
	}

	static async sendToDealer(inputValues: LeadInputValues, shortCode: string, dealerInfo: DealerDetails | null, leadType: string, value: number, hasUserConsent: boolean, hasMarketingOptin: boolean) {

		// set the form label and model accordingly.
		const formLabel = Configuration.isSupersport400Model() ? 'configurator_supersport_400' : 'configurator_supersport';
		const model = Configuration.isSupersport400Model() ? 'Supersport 400' : 'Supersport';

		// call the web service.
		const sendToDealer = await WebService.sendToDealer(inputValues, shortCode, dealerInfo?.id || '', formLabel, model, leadType, value, hasUserConsent, hasMarketingOptin);
		return sendToDealer;
	}

	// save the configuration.
	static async saveConfiguration() {

		// generate the summary and options.
		const summary = await this.generateSummary();
		const options = await this.generateOptions();

		const saveConfigObj = {
			options,
			summary,
		}

		// log the object to be saved for debugging!!
		//console.log(saveConfigObj);

		// call the web service.
		return await WebService.saveConfiguration(Configuration.regionCode, saveConfigObj);
	}

	static async generatePdf(shortCode: string, images: Blob[], email: string, firstName: string, lastName: string, sendEmailTemplate?: string, dealerName?: string, dealerCompanyName?: string, dealerEmail?: string) {

		// set the model accordingly.
		const model = Configuration.isSupersport400Model() ? 'supersport-400' : 'supersport';

		// generate options.
		const options = await this.generateOptions();
		const summary = await this.generateSummary();
		summary.shift();

		// setup in the way the server expects.
		const pdfOptions = {
			'options': options,
			'summary': summary
		}

		// save images.
		const res = await WebService.saveImages(images);
		if (!res) return null;

		// convert object to array.
		const imageUrls = Object.values(res) as unknown as string[];

		// call the web service.
		return await WebService.generatePdf(Configuration.regionCode, Configuration.languageCode || 'en', shortCode, model, pdfOptions, imageUrls, email, firstName, lastName, sendEmailTemplate, dealerName, dealerCompanyName, dealerEmail);
	}

	static getPrice(id: string, options: Options, isInvalidOption: {ref: boolean}): number {

		// handle the special ID of 'NONE' and 'N/A', which denotes a valid option with no corresponding entry in the options table.
		if (id === 'NONE' || id ==='N/A') {
			return 0;
		}

		// get the price from the relevant option, returning zero if not found or not valid.
		let price = 0;
		const option = this.getOption(id, options);
		if (option) {
			price = option.price;

			// a value of "-1" means the option is not valid in the current region, so return the price as zero and set the given flag.
			if (price === -1) {
				isInvalidOption.ref = true;
				price = 0;
			}
		}
		return price;
	}

	static getAccessoryPrices(accessoryOptions: Set<string>, isInvalidOption: { ref: boolean }): number {
		let price = 0;
		accessoryOptions.forEach((item) => {
			price += this.getPrice(item, this.accessoryOptions, isInvalidOption);
		});
		return price;
	}

	static getName(id: string, options: Options): string {

		// handle the special ID of 'NONE', which denotes a valid option with no corresponding entry in the options table.
		if (id === 'NONE') {
			return 'None';
		}

		// handle the special ID of 'N/A', which denotes a valid option with no corresponding entry in the options table.
		if (id === 'N/A') {
			return 'N/A';
		}

		// look up the ID in the options, and return the relevant option name.
		const option = this.getOption(id, options);
		if (option) {
			return option.name;
		}

		// or, log an error if not found, and return an empty string.
		console.error(`ID: ${id} cannot be found in the options data`);
		return '';
	};

	static getDescription(id: string, options: Options): string {

		// handle the special ID of 'NONE' and 'N/A', which denotes a valid option with no corresponding entry in the options table.
		if (id === 'NONE' || id === 'N/A') {
			return '';
		}

		// look up the ID in the options, and return the relevant option description.
		const option = this.getOption(id, options);
		if (option) {
			return option.description;
		}

		// or, log an error if not found, and return an empty string.
		console.error(`ID: ${id} cannot be found in the options data`);
		return '';
	};

	static getModalContentItem(id: string, options: Options) {

		// get description and name.
		const description = this.getDescription(id, options);
		const name = this.getName(id, options);

		// return nothing if there is no description.
		if (!description) return null;

		const content = {
			title: name,
			body: description
		}

		// return the content object to be used by a modal.
		return content;
	}

	static getModalContent(id: string, options: Options) {

		// get content for modal and return it as an array so it can be correctly processed by Modal.tsx.
		const item = this.getModalContentItem(id, options);

		if (!item) return [];

		return [item];
	}

	static getAccessoryModalContent(selectedOptions: Set<string>) {
		const content: ModalContent[] = [];

		// handle multiple accessories and return as an array of objects.
		selectedOptions.forEach((item) => {
			const itemDescription = this.getModalContentItem(item, this.accessoryOptions);
			if (itemDescription) {
				content.push(itemDescription);
			}
		});

		return content;
	}

	static getAccessoryDescriptions(selectedOptions: Set<string>): string[] {

		// create array of descriptions as multiple accessories from the same section can be selected.
		const descriptions: string[] = [];

		selectedOptions.forEach((item) => {
			const itemDescription = this.getDescription(item, this.accessoryOptions);
			if (itemDescription) {
				descriptions.push(itemDescription);
			}
		});

		return descriptions;
	}

	static getOption(id: string, options: Options): Option | null {

		// look up the ID in the options, and return the relevant option.
		const option = options[id];
		if (option) {
			return option;
		}

		// or, log an error if not found, and return null.
		console.error(`ID: ${id} cannot be found in the options data`);
		return null;
	}

	static getPaintTypeForId(id: string): PaintTypes {

		// n.b. the bespoke ids belong to the paint type whose panel offers them, so that a bespoke colour opens that panel and is
		// summarised under that heading, exactly as one of its fixed colours would be.
		const solidIds: readonly string[] = [
			'PS006', 'PS004', 'PS001', 'PS002', 'PS003', 'PS005', 'PS014', 'PS015', 'PS013', 'PS012', 'PS017', 'PS018',
			'PS022', 'PS023', BespokePaintColours.Solid
		];

		const metallicIds: readonly string[] = [
			'PM001', 'PM003', 'PM008', 'PM005', 'PM002', 'PM007', 'PM006', 'PM013', 'PM014', 'PM015', 'PM019', 'PM020', 'PM024', 'PM025', 'PM023',
			'PM027', 'PM028', 'PM029', BespokePaintColours.Metallic
		];

		const solitallicIds: readonly string[] = ['PSM001', 'PSM002', 'PSM003', 'PM026'];

		const satinIds: readonly string[] = ['ST01', 'ST02', 'ST03', 'ST04'];

		if (solidIds.indexOf(id) !== -1) {
			return PaintTypes.Solid;
		}
		if (metallicIds.indexOf(id) !== -1) {
			return PaintTypes.Metallic;
		}
		if (solitallicIds.indexOf(id) !== -1) {
			return PaintTypes.Solitallic;
		}
		if (satinIds.indexOf(id) !== -1) {
			return PaintTypes.Satin;
		}

		// default to solid if not found.
		return PaintTypes.Solid;
	}

	static getWheelTypeForId(id: string): WheelTypes {

		const superliteIds: readonly string[] = [
			'W033', 'W034', 'W035'
		];

		const multispokeIds: readonly string[] = [
			'W003', 'W004', 'W026'
		];

		const aeroliteIds: readonly string[] = [
			'W036', 'W037'
		];

		const sportliteIds: readonly string[] = [
			'W038', 'W039'
		];

		if (superliteIds.indexOf(id) !== -1) {
			return WheelTypes.Superlite;
		}
		if (multispokeIds.indexOf(id) !== -1) {
			return WheelTypes.Multispoke;
		}
		if (aeroliteIds.indexOf(id) !== -1) {
			return WheelTypes.Aerolite;
		}
		if (sportliteIds.indexOf(id) !== -1) {
			return WheelTypes.Sportlite;
		}

		// default to superlite if not found.
		return WheelTypes.Superlite;
	}

	static getLeatherTypesForId(id: string): { leatherType: LeatherTypes, leatherSubType: LeatherTypes } {

		// group IDs by type (or by type and subtype for two-tone).
		const pebbleGrainIds: readonly string[] = [
			'LP001', 'LP003', 'LP002', 'LP013', 'LP010', 'LP011', 'LP006', 'LP009'
		];
		const softGrainIds: readonly string[] = [
			'LS002', 'LS003', 'LS001', 'LS004', 'LS005', 'LS010', 'LS008', 'LS009', 'LP007', 'LS011', 'LP004'
		];
		const twoTonePebbleGrainIds: readonly string[] = [
			'LP018', 'LP019', 'LP020', 'LP026', 'LP028'
		];
		const twoToneSoftGrainIds: readonly string[] = [
			'LP021', 'LP024', 'LP023', 'LP027', 'LP029'
		];
		
		// default to two-tone / pebble grain.
		let leatherType = LeatherTypes.TwoTone;
		let leatherSubType = LeatherTypes.PebbleGrain;
		
		if (pebbleGrainIds.indexOf(id) !== -1) {
			leatherType = LeatherTypes.PebbleGrain;
			leatherSubType = LeatherTypes.PebbleGrain;
		}
		else if (softGrainIds.indexOf(id) !== -1) {
			leatherType = LeatherTypes.SoftGrain;
			leatherSubType = LeatherTypes.SoftGrain;
		}
		else if (twoTonePebbleGrainIds.indexOf(id) !== -1) {
			leatherType = LeatherTypes.TwoTone;
			leatherSubType = LeatherTypes.PebbleGrain;
		}
		else if (twoToneSoftGrainIds.indexOf(id) !== -1) {
			leatherType = LeatherTypes.TwoTone;
			leatherSubType = LeatherTypes.SoftGrain;
		}
		return { leatherType, leatherSubType }
	}

	static getLuggageRackAccessories(): Set<string> {
		return new Set<string>(['AM044', 'AM045']);
	}
}